import { CashReceiptResponse } from "../../services/cashReceiptService";
import { amountToWords } from "../../utils/posPrintRenderer";

const PRINT_FRAME_ID = "cash-receipt-print-frame";

export interface CashReceiptPrintHeader {
    companyName: string;
    companyAddress: string;
    companyTele: string;
    locationLabel: string;
}

export interface CashReceiptReportMeta extends CashReceiptPrintHeader {
    date: string;
    counter: string;
    shiftLabel: string;
    printedBy: string;
}

export const CASH_MODE_LABELS: Record<string, string> = {
    R: "Receipt",
    W: "Withdrawal",
};

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

export const formatMoney = (value: number | null | undefined): string =>
    Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pad = (value: number) => String(value).padStart(2, "0");

const formatDateTime = (date: Date): string => {
    const hours = date.getHours();
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
        `${pad(hours % 12 || 12)}:${pad(date.getMinutes())} ${hours < 12 ? "AM" : "PM"}`;
};

// Server times arrive as "YYYY-MM-DDTHH:MM:SS" in server-local time; show them as-is.
export const formatServerTime = (value: string | null | undefined): string => {
    const match = /^\d{4}-\d{2}-\d{2}[ T](\d{2}):(\d{2})/.exec(value || "");
    if (!match) {
        return "-";
    }
    const hours = Number(match[1]);
    return `${pad(hours % 12 || 12)}:${match[2]} ${hours < 12 ? "AM" : "PM"}`;
};

export const formatServerDate = (value: string | null | undefined): string => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value || "-";
};

const formatServerDateTime = (value: string | null | undefined): string =>
    `${formatServerDate(value)} ${formatServerTime(value)}`;

const STYLES = `
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @page { size: 80mm auto; margin: 0; }
        body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000; }
        .slip { width: 72mm; margin: 0 auto; padding: 3mm 1mm 6mm; overflow-wrap: anywhere; }
        .center { text-align: center; }
        .company-name { font-size: 14px; font-weight: 700; }
        .title { font-size: 13px; font-weight: 700; margin-top: 4px; letter-spacing: 0.5px; }
        .reprint { font-weight: 700; border: 1px solid #000; display: inline-block; padding: 0 6px; margin-top: 3px; }
        hr { border: none; border-top: 1px dashed #000; margin: 5px 0; }
        hr.solid { border-top: 1px solid #000; }
        .row { display: flex; justify-content: space-between; gap: 6px; margin: 1px 0; }
        .label { width: 21mm; flex-shrink: 0; }
        .value { flex: 1; text-align: left; }
        .amount-row { display: flex; justify-content: space-between; font-size: 14px; font-weight: 700; margin: 3px 0; }
        .words { font-style: italic; margin-top: 2px; }
        .signatures { display: flex; justify-content: space-between; margin-top: 30px; }
        .signature { width: 30mm; border-top: 1px dotted #000; text-align: center; padding-top: 2px; font-size: 10px; }
        .footer { margin-top: 8px; font-size: 10px; text-align: center; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 1px 0; vertical-align: top; }
        th { text-align: left; border-bottom: 1px solid #000; font-weight: 700; }
        .num { text-align: right; white-space: nowrap; }
        .sub td { font-size: 10px; padding-bottom: 3px; }
        .section { font-weight: 700; margin-top: 4px; }
        .total td { font-weight: 700; border-top: 1px solid #000; padding-top: 2px; }
        .empty td { font-style: italic; padding: 2px 0 4px; }
    </style>
`;

const companyHeader = (header: CashReceiptPrintHeader): string => `
    <div class="center">
        <div class="company-name">${escapeHtml(header.companyName || "-")}</div>
        ${header.companyAddress ? `<div>${escapeHtml(header.companyAddress)}</div>` : ""}
        ${header.companyTele ? `<div>Tel: ${escapeHtml(header.companyTele)}</div>` : ""}
    </div>`;

const infoRow = (label: string, value: string): string =>
    `<div class="row"><span class="label">${escapeHtml(label)}</span><span class="value">: ${escapeHtml(value || "-")}</span></div>`;

const wrapDocument = (title: string, body: string): string => `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <title>${escapeHtml(title)}</title>
        ${STYLES}
    </head>
    <body><div class="slip">${body}</div></body>
    </html>
`;

export const generateCashReceiptSlipHtml = (
    receipt: CashReceiptResponse,
    header: CashReceiptPrintHeader,
    isReprint = false
): string => {
    const isWithdrawal = receipt.payment_mode === "W";
    const title = isWithdrawal ? "CASH WITHDRAWAL" : "CASH RECEIPT";
    const amount = Number(receipt.payment_amount || 0);

    return wrapDocument(title, `
        ${companyHeader(header)}
        <hr class="solid" />
        <div class="center">
            <div class="title">${title}</div>
            ${isReprint ? `<div class="reprint">REPRINT</div>` : ""}
        </div>
        <hr />
        ${infoRow("No", String(receipt.payment_no))}
        ${infoRow("Date", formatServerDateTime(receipt.payment_time || receipt.payment_date))}
        ${infoRow("Location", header.locationLabel || receipt.payment_loc || "-")}
        ${infoRow("Counter", receipt.payment_station || "-")}
        ${infoRow("Shift", receipt.payment_shift != null ? String(receipt.payment_shift) : "-")}
        ${infoRow("Cashier", receipt.payment_cashier_id || "-")}
        ${receipt.payment_description ? infoRow("Description", receipt.payment_description) : ""}
        <hr />
        <div class="amount-row"><span>${isWithdrawal ? "AMOUNT PAID OUT" : "AMOUNT RECEIVED"}</span><span>${formatMoney(amount)}</span></div>
        <div class="words">${escapeHtml(amountToWords(amount))}</div>
        <hr />
        <div class="signatures">
            <div class="signature">Cashier</div>
            <div class="signature">${isWithdrawal ? "Received by" : "Authorized by"}</div>
        </div>
        <div class="footer">Printed: ${escapeHtml(formatDateTime(new Date()))}</div>
    `);
};

export const generateCashReceiptReportHtml = (
    receipts: CashReceiptResponse[],
    meta: CashReceiptReportMeta
): string => {
    // Oldest first on paper
    const ordered = [...receipts].sort((a, b) => a.payment_no - b.payment_no);
    const total = (rows: CashReceiptResponse[]) => rows.reduce((sum, row) => sum + Number(row.payment_amount || 0), 0);

    const section = (label: string, rows: CashReceiptResponse[]): string => {
        const body = rows.length === 0
            ? `<tr class="empty"><td colspan="4">None</td></tr>`
            : rows.map((row) => `
                <tr>
                    <td>${row.payment_no}</td>
                    <td>${escapeHtml(formatServerTime(row.payment_time))}</td>
                    <td class="num">${row.payment_shift ?? "-"}</td>
                    <td class="num">${formatMoney(row.payment_amount)}</td>
                </tr>
                <tr class="sub">
                    <td colspan="4">${escapeHtml(row.payment_cashier_id || "-")}${row.payment_description ? ` &ndash; ${escapeHtml(row.payment_description)}` : ""}</td>
                </tr>`).join("");

        return `
            <div class="section">${escapeHtml(label)}</div>
            <table>
                <thead><tr><th>No</th><th>Time</th><th class="num">Shift</th><th class="num">Amount</th></tr></thead>
                <tbody>
                    ${body}
                    <tr class="total"><td colspan="3">Total ${escapeHtml(label.toLowerCase())} (${rows.length})</td><td class="num">${formatMoney(total(rows))}</td></tr>
                </tbody>
            </table>`;
    };

    const receiptsIn = ordered.filter((row) => row.payment_mode === "R");
    const withdrawals = ordered.filter((row) => row.payment_mode === "W");
    const net = total(receiptsIn) - total(withdrawals);

    return wrapDocument("Cash Receipts Report", `
        ${companyHeader(meta)}
        <hr class="solid" />
        <div class="center"><div class="title">CASH RECEIPTS &amp; WITHDRAWALS</div></div>
        <hr />
        ${infoRow("Date", formatServerDate(meta.date))}
        ${infoRow("Location", meta.locationLabel)}
        ${infoRow("Counter", meta.counter)}
        ${infoRow("Shift", meta.shiftLabel)}
        <hr />
        ${section("Receipts", receiptsIn)}
        <hr />
        ${section("Withdrawals", withdrawals)}
        <hr class="solid" />
        <div class="amount-row"><span>NET CASH ${net < 0 ? "OUT" : "IN"}</span><span>${formatMoney(Math.abs(net))}</span></div>
        <hr class="solid" />
        <div class="footer">Printed by ${escapeHtml(meta.printedBy || "-")} on ${escapeHtml(formatDateTime(new Date()))}</div>
    `);
};

// Prints from an off-screen iframe (like the POS bill), so it works after the async save
// without tripping the popup blocker.
const printHtml = (html: string, onError?: (message: string) => void) => {
    document.getElementById(PRINT_FRAME_ID)?.remove();

    const frame = document.createElement("iframe");
    frame.id = PRINT_FRAME_ID;
    frame.setAttribute("aria-hidden", "true");
    frame.tabIndex = -1;
    Object.assign(frame.style, {
        position: "fixed",
        left: "-10000px",
        top: "0",
        width: "80mm",
        height: "600px",
        border: "0",
    });

    frame.onload = () => {
        const frameWindow = frame.contentWindow;
        if (!frameWindow) {
            frame.remove();
            onError?.("Failed to prepare the print document.");
            return;
        }
        frameWindow.addEventListener("afterprint", () => setTimeout(() => frame.remove(), 0));
        try {
            frameWindow.focus();
            frameWindow.print();
        } catch {
            frame.remove();
            onError?.("Failed to open the print dialog.");
        }
    };

    frame.srcdoc = html;
    document.body.appendChild(frame);
};

export const printCashReceiptSlip = (
    receipt: CashReceiptResponse,
    header: CashReceiptPrintHeader,
    options?: { isReprint?: boolean; onError?: (message: string) => void }
) => printHtml(generateCashReceiptSlipHtml(receipt, header, options?.isReprint), options?.onError);

export const printCashReceiptReport = (
    receipts: CashReceiptResponse[],
    meta: CashReceiptReportMeta,
    onError?: (message: string) => void
) => printHtml(generateCashReceiptReportHtml(receipts, meta), onError);
